import { ActionButton, Text } from '@react-spectrum/s2';
import Undo from '@react-spectrum/s2/icons/Undo';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { BlobImage } from '@/components/blob/BlobImage';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';
import { trackEvent } from '@/utils/fullstory';

import type { Artifact } from '../../../../../bridge/types/artifact.ts';
import type { RemovedSlideArtifact as RemovedSlideArtifactMessage } from '../../../../../bridge/types/messages.ts';

import { MessageRow } from './MessageRow.tsx';

interface RemovedSlideArtifactProps {
	artifactId: string;
	removedImageUrl: string;
	removedIndex: number;
	priorContent: RemovedSlideArtifactMessage['prior_content'];
	resultImageUrls: string[];
}

function getArtifactImageUrls(
	panelArtifact: Artifact | null | undefined,
	artifactId: string,
): string[] | null {
	if (!panelArtifact || panelArtifact.id !== artifactId) return null;
	const urls = (panelArtifact.content as Record<string, unknown>).image_urls;
	return Array.isArray(urls) ? (urls as string[]) : null;
}

function sameUrls(a: string[], b: string[]): boolean {
	if (a.length !== b.length) return false;
	return a.every((url, i) => url === b[i]);
}

export const RemovedSlideArtifact = ({
	artifactId,
	removedImageUrl,
	removedIndex,
	priorContent,
	resultImageUrls,
}: RemovedSlideArtifactProps) => {
	const bridgeApi = useBridgeApi();
	const openChatPanel = useOpenChatPanel();
	const panelArtifact =
		bridgeApi.user_state?.chat_panel?.panel_type === 'artifact'
			? bridgeApi.user_state.chat_panel.artifact
			: null;

	const liveImageUrls = getArtifactImageUrls(panelArtifact, artifactId);
	// Only offer Undo while the panel still shows exactly the post-delete state.
	// If the user edited, reordered, or added a slide since, re-inserting the
	// removed slide by its old index would corrupt the wrong position, so the
	// control disappears rather than risk it.
	const showUndo =
		liveImageUrls != null && sameUrls(liveImageUrls, resultImageUrls);

	const handleUndo = () => {
		trackEvent('Slide Removal Undone', {
			artifact_id: artifactId,
			image_index: removedIndex,
		});
		// Whole-replace the parallel arrays back to their pre-delete state.
		// do_edit_artifact deep-merges, and arrays replace wholesale under it, so
		// this restores the removed slide exactly without re-running index math.
		bridgeApi.sendAction({
			type: 'do_edit_artifact',
			artifact_id: artifactId,
			edits: priorContent,
		});
		// Re-point the view at the slide just restored; the delete left the
		// selection clamped into the shorter array.
		bridgeApi.sendAction({
			type: 'do_set_chat_panel_slide',
			slide_index: removedIndex,
		});
	};

	const handleOpenPanel = () => {
		trackEvent('Artifact Clicked', {
			panel_type: 'artifact',
			artifact_id: artifactId,
			image_index: removedIndex,
		});
		openChatPanel({
			panel_type: 'artifact',
			item_id: artifactId,
		});
	};

	const label = `Slide ${removedIndex + 1} removed`;

	return (
		<MessageRow>
			<div
				className="glass-card-sm glass-card-hover flex cursor-pointer gap-4 px-4
					py-3"
				role="button"
				tabIndex={0}
				aria-label={`Open ${label} in panel`}
				onClick={handleOpenPanel}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						// Space would otherwise scroll the page while the card has focus.
						e.preventDefault();
						handleOpenPanel();
					}
				}}
			>
				<div className="flex items-center gap-2">
					{/*
						Undo is a real <button> whose activation (click and keydown)
						bubbles up the DOM. Stop it here so pressing it doesn't also
						trigger the card's open-panel handler.
					*/}
					<span
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
					>
						{showUndo && (
							<ActionButton
								isQuiet
								onPress={handleUndo}
								aria-label="Undo slide removal"
								size="XS"
							>
								<Undo />
							</ActionButton>
						)}
					</span>
					<Text
						styles={style({
							font: 'body',
							color: 'body',
							fontSize: 'body',
						})}
					>
						{label}
					</Text>
				</div>
				{removedImageUrl && (
					<BlobImage
						src={removedImageUrl}
						alt="Removed slide"
						className="h-10 w-10 rounded-lg object-cover"
					/>
				)}
			</div>
		</MessageRow>
	);
};
